/**
 * V70 타자왕: 산성비 — 메인 게임 페이지 (Phase 2)
 *
 * 3 Phase UI:
 *   1. 대기 화면 (ready): 설명 + 코인/최고점 + 시작 버튼
 *   2. 플레이 (playing): 낙하 단어 + HUD + 입력창
 *   3. 종료 (ended): 결과 요약 + 다시 하기 / 리더보드
 *
 * Phase 2 데모 명시 사항:
 *   - 실제 코인 차감/점수 저장 X (mock 만)
 *   - 모든 플레이는 "데모 모드" 로 표시
 *   - 리더보드도 샘플
 *
 * 사용자 지시 반영:
 *   - 일시정지 없음 (포커스 이탈/탭 전환 무관하게 시간 흐름)
 *   - 한국어 IME 지원 (compositionstart/end)
 *   - 자동 입력 방지 보장 과장 X — 사전은 공개됨
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Flame, Heart, Clock, Zap, X, Target, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useAcidRainEngine, type AcidRainServerInputEvent } from './acidRain/useAcidRainEngine';
import { acidRainApi } from './acidRain/serverApi';
import type { AcidRainSettings, DailyCoinStatus, GameResult } from './acidRain/types';
// Phase 3-C.2 보완 — cancel token: 언마운트 시 재시도 backoff sleep 즉시 중단
import { createCancelToken, type CancelToken } from './acidRain/server/serverApi';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
// V70 2026-10-19 디자인 토큰 — 미니멀 프리미엄 아케이드 (차콜/블루그레이/샴페인 골드)
import { ACIDRAIN_COLORS as C } from './shared/designTokens';
// V70G8I — 연습 게임 (서버 미호출: 토큰 차감·기록 저장 없음)
import { computePracticeStats, createPracticeSession, type PracticeStats } from './acidRain/practice';
import { attachPasteGuard } from './acidRain/pasteGuard';
// V70-I — 대회 기간 밖(전·후)에는 공식 도전만 막고 연습은 허용
import { computeGamePeriod, type GamePeriodState } from './shared/gamePeriod';

type Phase = 'loading' | 'ready' | 'countdown' | 'playing' | 'ended';

interface SessionBundle {
  sessionId: string;
  seed: string;
  startedAtServerMs: number;
  settings: AcidRainSettings;
  leaderboardEpoch: number;
  /** V70G8I: 연습 세션 — 브라우저에서 생성, 서버 기록 제출 금지 */
  practice?: boolean;
}

/** V70G8I: 모드 안내 문구 (대기 화면) */
export const OFFICIAL_MODE_NOTE = '오늘 남은 도전 1회가 차감되며, 점수가 공식 기록과 리더보드에 반영됩니다.';
export const PRACTICE_MODE_NOTE = '도전 횟수가 차감되지 않으며, 기록·리더보드에 반영되지 않습니다. 횟수 제한 없이 연습할 수 있습니다.';
export const PRACTICE_RESULT_NOTE = '연습 결과 · 공식 기록에 반영되지 않습니다';
/** V70-I: 대회 기간 밖 안내 (공식 도전 불가, 연습만 가능) */
export const OFFICIAL_BEFORE_NOTE = '아직 대회 기간이 아니어서 공식 도전은 할 수 없습니다. 연습 게임은 지금 이용할 수 있습니다.';
export const OFFICIAL_AFTER_NOTE = '대회가 종료되어 공식 도전은 할 수 없습니다. 연습 게임은 계속 이용할 수 있습니다.';

/** V70-I: 설정의 공개 여부·기간으로 공식 도전 가능 상태 계산 (연습 가능 여부와 무관) */
function officialPeriodState(s: AcidRainSettings | null): GamePeriodState | null {
  if (!s) return null;
  return computeGamePeriod({ enabled: s.enabled, openStartAt: s.openStartAt ?? null, openEndAt: s.openEndAt ?? null }).state;
}

type SaveState =
  | { status: 'idle' }
  | { status: 'saving' }
  // F3 정책: none + low 는 공식 반영 (success). low 는 flaggedLow=true 로 보조 안내.
  | { status: 'success'; isPersonalBest: boolean; rank: number | null; flaggedLow?: boolean; serverVerifiedScore?: number | null }
  // Phase 3-C.1 결함 A — flagged (invalid/high/macro): 공식 기록 보류 (F3)
  | { status: 'flagged'; reason: 'high' | 'macro' | 'invalid'; serverVerifiedScore: number | null }
  | { status: 'error'; message: string };

const FIELD_HEIGHT_PX = 480;

export function AcidRainGamePage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [phase, setPhase] = useState<Phase>('loading');
  const [settings, setSettings] = useState<AcidRainSettings | null>(null);
  const [coinStatus, setCoinStatus] = useState<DailyCoinStatus | null>(null);
  const [myBestScore, setMyBestScore] = useState<number | null>(null);
  const [session, setSession] = useState<SessionBundle | null>(null);
  const [result, setResult] = useState<GameResult | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [countdownNum, setCountdownNum] = useState<number>(3);
  const [saveState, setSaveState] = useState<SaveState>({ status: 'idle' });
  // V70G8I — 연습 결과 지표 (정확도용 오답 제출 수는 플레이 화면에서 집계)
  const [practiceStats, setPracticeStats] = useState<PracticeStats | null>(null);
  const wrongSubmitsRef = useRef<number>(0);
  // clientRequestId 는 세션별 1개로 고정 — 재시도 시에도 동일 유지해 중복 저장 방지
  const submitRequestIdRef = useRef<string | null>(null);
  // Phase 3-C.2 — 재시도 시 원본 inputEvents 유지 (engine 소멸 후에도 접근 가능)
  const inputEventsRef = useRef<AcidRainServerInputEvent[] | null>(null);
  // Phase 3-C.2 — 중복 클릭 및 동시 재시도 요청 차단
  const isSubmittingRef = useRef<boolean>(false);
  // V70G5A Bug 2 — handleStart 재진입 가드: 빠른 중복 클릭 시 서버 호출이 매번 새 clientRequestId 로
  //   발급되어 BE idempotency 가 통하지 않으므로 FE 에서 in-flight 상태를 ref 로 즉시 차단.
  //   buttons 는 isStarting state 로 disable 하여 2중 안전.
  const isStartingRef = useRef<boolean>(false);
  const [isStarting, setIsStarting] = useState<boolean>(false);
  // Phase 3-C.2 — 언마운트 시 재시도 중단 + setState 가드
  const unmountedRef = useRef<boolean>(false);
  // Phase 3-C.2 보완 — in-flight submitScore 호출의 재시도 backoff sleep 즉시 중단용 cancel token.
  // 언마운트 시 cancelled=true. 정상 완료·수동 재시도 시 새 토큰으로 교체 (잘못된 cancelled 유지 방지).
  const cancelTokenRef = useRef<CancelToken | null>(null);
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (cancelTokenRef.current) cancelTokenRef.current.cancelled = true;
    };
  }, []);

  // 초기 로드 (설정 + 코인 + 최고점)
  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    (async () => {
      try {
        const [s, c, b] = await Promise.all([
          acidRainApi.getSettings(),
          acidRainApi.getDailyCoinStatus(),
          acidRainApi.getMyBestScore(user.profile.id),
        ]);
        if (cancelled) return;
        setSettings(s);
        setCoinStatus(c);
        setMyBestScore(b.bestScore);
        setPhase('ready');
      } catch (err) {
        if (cancelled) return;
        setStartError(err instanceof Error ? err.message : String(err));
        setPhase('ready');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleStart = useCallback(async () => {
    // V70G5A Bug 2 — 중복 클릭 차단: ref 로 즉시 재진입 거부. setState 는 비동기라 ref 로 동기 체크.
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    setIsStarting(true);
    setStartError(null);
    if (!settings || !settings.enabled) {
      setStartError('현재 엔글 타자왕은 비공개 상태입니다.');
      isStartingRef.current = false;
      setIsStarting(false);
      return;
    }
    // V70-I — 공식 도전은 대회 기간 안에서만 (서버 consumeAcidRainSession 도 기간 밖이면 거부)
    const per = officialPeriodState(settings);
    if (per === 'before' || per === 'after') {
      setStartError(per === 'before' ? OFFICIAL_BEFORE_NOTE : OFFICIAL_AFTER_NOTE);
      isStartingRef.current = false;
      setIsStarting(false);
      return;
    }
    try {
      const clientRequestId = `creq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const r = await acidRainApi.startSession({ clientRequestId });
      setSession({
        sessionId: r.sessionId,
        seed: r.seed,
        startedAtServerMs: r.startedAtServerMs,
        settings: r.settings,
        leaderboardEpoch: r.leaderboardEpoch,
      });
      // 세션별 submit clientRequestId 를 미리 생성 — 재시도 시에도 동일 유지 (중복 방지)
      submitRequestIdRef.current = `submit-${r.sessionId}-${Math.random().toString(36).slice(2, 10)}`;
      setSaveState({ status: 'idle' });
      // coin 다시 조회
      const c = await acidRainApi.getDailyCoinStatus();
      setCoinStatus(c);
      setCountdownNum(3);
      setPhase('countdown');
    } catch (err) {
      setStartError(err instanceof Error ? err.message : String(err));
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  }, [settings]);

  // V70G8I — 연습 게임 시작: 서버 호출 없음 (consumeAcidRainSession·getDailyCoinStatus 미호출 → 토큰 미차감).
  //   토큰 0개여도 시작 가능. V70-I — 대회 기간 전·중·후 모두 가능, 게임대회 OFF(settings.enabled=false)만 차단.
  const handleStartPractice = useCallback(() => {
    if (isStartingRef.current) return;
    setStartError(null);
    if (!settings || !settings.enabled) {
      setStartError('현재 엔글 타자왕은 비공개 상태입니다.');
      return;
    }
    const s = createPracticeSession(settings);
    setSession(s);
    submitRequestIdRef.current = null;
    inputEventsRef.current = null;
    wrongSubmitsRef.current = 0;
    setSaveState({ status: 'idle' });
    setPracticeStats(null);
    setResult(null);
    setCountdownNum(3);
    setPhase('countdown');
  }, [settings]);

  const runSubmit = useCallback(async (sess: SessionBundle, r: GameResult, inputEvents?: AcidRainServerInputEvent[]) => {
    // V70G8I — 연습 세션은 어떤 경우에도 공식 기록 제출 금지
    if (sess.practice) return;
    if (!user) {
      setSaveState({ status: 'error', message: '로그인 정보가 없어 점수를 저장할 수 없습니다.' });
      return;
    }
    // Phase 3-C.2 — 중복 클릭·동시 재시도 차단
    if (isSubmittingRef.current) {
      return;
    }
    const reqId = submitRequestIdRef.current ?? `submit-${sess.sessionId}`;
    submitRequestIdRef.current = reqId;
    // Phase 3-C.2 — inputEvents ref 에 보관 (수동 재시도 때 사용)
    if (inputEvents && inputEvents.length > 0) inputEventsRef.current = inputEvents;
    isSubmittingRef.current = true;
    // Phase 3-C.2 보완 — 매 submit 시마다 새 cancel token (이전 성공·실패 때 cancelled 상태 리셋)
    const token = createCancelToken();
    cancelTokenRef.current = token;
    setSaveState({ status: 'saving' });
    try {
      const resp = await acidRainApi.submitScore({
        sessionId: sess.sessionId,
        clientRequestId: reqId,
        result: r,
        sessionEpoch: sess.leaderboardEpoch,
        cancelToken: token,
        user: {
          uid: user.profile.id,
          // ISSUE-01: nameKo 를 우선 (Firestore users.nameKo 가 실제 한글 이름 SSoT).
          //           displayName 은 Firebase Auth 측 보조 필드로만 사용. 하드코딩 fallback 금지.
          displayName: (user.profile as any).nameKo || user.profile.displayName || null,
          companyCode:
            user.profile.companyCode === 'KR' || user.profile.companyCode === 'CN'
              ? user.profile.companyCode
              : undefined,
          // V70: 저장 시점 소속 조직 ID. UI 가 조회 시점에 조직명 매핑
          orgUnitId: user.profile.orgUnitId,
        },
        // Phase 3-C.1 결함 A — 서버 재계산용 입력 이벤트. Mock serverApi 는 무시.
        inputEvents,
      });
      // Phase 3-C.1 결함 A — flagged 분기 UI (사용자 지시 §A, §invalid/flagged UI 명확).
      // F3 정책 (사용자 지시 §1): low 는 공식 리더보드에 반영됨 → success 로 분류.
      //   high/invalid/macro 만 flagged (공식 보류).
      const isOfficialAccepted = !resp.flagged || resp.flagged === 'none' || resp.flagged === 'low';
      if (isOfficialAccepted) {
        setSaveState({
          status: 'success',
          isPersonalBest: !!resp.isPersonalBest,
          rank: resp.rank ?? null,
          // low 는 서버 재계산 반영 안내 추가 (사용자 지시 §6)
          flaggedLow: resp.flagged === 'low',
          serverVerifiedScore: resp.serverVerifiedScore ?? null,
        });
      } else {
        // 공식 보류: resp.flagged 는 high/invalid/macro 중 하나 (타입 narrowing)
        setSaveState({
          status: 'flagged',
          reason: resp.flagged as 'high' | 'invalid' | 'macro',
          serverVerifiedScore: resp.serverVerifiedScore,
        });
      }
      // 개인 최고점 반영 (현재 epoch 기준)
      const b = await acidRainApi.getMyBestScore(user.profile.id);
      setMyBestScore(b.bestScore);
    } catch (err) {
      // 언마운트 후 setState 금지 (cancel 로 중단된 경우 포함)
      if (unmountedRef.current) return;
      setSaveState({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      isSubmittingRef.current = false;
      // Phase 3-C.2 보완 — 정상 완료 시 cancel token 해제 (다음 수동 재시도가 새 토큰으로 시작)
      if (cancelTokenRef.current === token) cancelTokenRef.current = null;
    }
  }, [user]);

  const handleRetrySubmit = useCallback(() => {
    // Phase 3-C.2 — 수동 재시도: inputEventsRef 에 보관된 원본 inputEvents 재사용
    //   - 중복 클릭 차단은 runSubmit 안쪽 isSubmittingRef 가 담당
    //   - 서버 멱등: 같은 clientRequestId + 같은 chunk → BE 가 reused=true 반환
    if (session && result && !session.practice) {
      void runSubmit(session, result, inputEventsRef.current ?? undefined);
    }
  }, [session, result, runSubmit]);

  if (phase === 'loading') return <LoadingScreen />;
  if (phase === 'ready' || phase === 'ended') {
    return (
      <ReadyEndedScreen
        phase={phase}
        practiceResult={phase === 'ended' && session?.practice ? practiceStats : null}
        onPractice={handleStartPractice}
        onBackToReady={() => {
          setResult(null);
          setPracticeStats(null);
          setPhase('ready');
        }}
        settings={settings}
        coinStatus={coinStatus}
        myBestScore={myBestScore}
        startError={startError}
        result={result}
        saveState={saveState}
        isStarting={isStarting}
        onStart={handleStart}
        onRetrySubmit={handleRetrySubmit}
        onLeaderboard={() => navigate('/tournament/leaderboard')}
        onExit={() => navigate('/tournament/games')}
      />
    );
  }
  // countdown / playing — session 있어야 함
  if (!session) return <LoadingScreen />;
  return (
    <GamePlayScreen
      phase={phase}
      countdownNum={countdownNum}
      session={session}
      onCountdownTick={setCountdownNum}
      onCountdownDone={() => setPhase('playing')}
      onWrongSubmit={() => { wrongSubmitsRef.current += 1; }}
      onEnd={(r, inputEvents) => {
        setResult(r);
        if (session.practice) {
          // V70G8I — 연습: 결과만 계산해 표시. 기록 제출·최고점 재조회 없음
          setPracticeStats(computePracticeStats(r, inputEvents, wrongSubmitsRef.current));
          setPhase('ended');
          return;
        }
        setPhase('ended');
        // Phase 3-C.1 결함 A — engine 에서 수집한 inputEvents 함께 전달.
        void runSubmit(session, r, inputEvents);
      }}
      onForceQuit={() => {
        setResult(null);
        setPhase('ready');
      }}
    />
  );
}

// ─────────────────────── 로딩 ───────────────────────

function LoadingScreen() {
  return (
    <div style={pageStyle}>
      <div style={{ color: C.textMuted, fontSize: 16 }}>게임 환경 준비 중…</div>
    </div>
  );
}

// ─────────────────────── 대기/종료 화면 ───────────────────────

interface ReadyEndedScreenProps {
  phase: 'ready' | 'ended';
  // V70G5A Bug 2 — 중복 클릭 차단용 in-flight 플래그
  isStarting: boolean;
  settings: AcidRainSettings | null;
  coinStatus: DailyCoinStatus | null;
  myBestScore: number | null;
  startError: string | null;
  result: GameResult | null;
  saveState: SaveState;
  onStart: () => void;
  onRetrySubmit: () => void;
  onLeaderboard: () => void;
  onExit: () => void;
  /** V70G8I: 연습 종료 결과 (null 이면 공식 결과/대기 화면) */
  practiceResult: PracticeStats | null;
  onPractice: () => void;
  onBackToReady: () => void;
}

function ReadyEndedScreen(p: ReadyEndedScreenProps) {
  // V70G8I — 연습 결과 화면: 공식 저장 상태·리더보드 대신 연습 안내 + 다시 연습하기 / 돌아가기
  if (p.phase === 'ended' && p.result && p.practiceResult) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>
            <span style={{ color: C.text }}>엔글</span>
            <span style={{ color: C.text }}> </span>
            <span style={{ color: C.gold }}>타자왕</span>
          </h1>
          <div data-testid="acid-practice-result-note" role="status" style={practiceNoteStyle}>
            {PRACTICE_RESULT_NOTE}
          </div>
          <ResultSummary result={p.result} />
          <div style={resultGridStyle} data-testid="acid-practice-stats">
            <ResultCell label="타수 (분당)" value={p.practiceResult.typingSpeed.toLocaleString()} />
            <ResultCell
              label="정확도"
              value={p.practiceResult.accuracy != null ? `${p.practiceResult.accuracy}%` : '-'}
            />
            <ResultCell label="정답 / 오답 입력" value={`${p.practiceResult.correctSubmits} / ${p.practiceResult.wrongSubmits}`} />
          </div>
          <div style={{ color: C.textMuted, fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
            타수: 제거한 단어의 타건 수(두벌식) ÷ 플레이 시간(분) · 정확도: Enter 로 제출한 입력 중 정답 비율
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            <button style={practiceBtn(true)} onClick={p.onPractice} data-testid="acid-practice-again">
              다시 연습하기
            </button>
            <button style={ghostBtn} onClick={p.onBackToReady} data-testid="acid-practice-back">
              돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }
  const enabled = p.settings?.enabled ?? false;
  const isUnlimited = p.coinStatus?.mode === 'unlimited';
  const remaining = p.coinStatus?.remaining ?? 0;
  // V70-I — 대회 기간 밖이면 공식 도전 불가 (연습은 가능)
  const periodState = officialPeriodState(p.settings);
  const outsidePeriod = enabled && (periodState === 'before' || periodState === 'after');
  // V70G5A Bug 2 — in-flight 중엔 버튼 추가 비활성화 (ref 가드와 2중 안전)
  const canStart = enabled && !outsidePeriod && (isUnlimited || remaining > 0) && !p.isStarting;
  // ISSUE-02: 상태 구분 — 공개(enabled)되어 있지만 코인 소진인 경우만 '내일 재도전'.
  //           비공개(!enabled) 상태의 "관리자 미공개" 문구는 그대로 유지.
  const isCoinExhausted = enabled && !isUnlimited && remaining <= 0;
  // 저장 중일 땐 리더보드 이동 비활성 — 저장 완료 전 이동해 반영 누락되지 않게
  const canOpenLeaderboard = !(p.phase === 'ended' && p.saveState.status === 'saving');
  // V70G8I — 연습은 도전 횟수 검사 없음. 공식 시작 요청 중이거나 공식 점수 저장 중에는 비활성
  const canPractice = enabled && !p.isStarting && !(p.phase === 'ended' && p.saveState.status === 'saving');

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* V70 2026-10-19 — 타이틀 통일: 전체 TEXT 톤 + '타자왕' 만 GOLD 강조 (지시 §4) */}
        <h1 style={titleStyle}>
          <span style={{ color: C.text }}>엔글</span>
          <span style={{ color: C.text }}> </span>
          <span style={{ color: C.gold }}>타자왕</span>
        </h1>

        {p.phase === 'ended' && p.result ? (
          <>
            <ResultSummary result={p.result} />
            <SaveStateBadge state={p.saveState} onRetry={p.onRetrySubmit} />
          </>
        ) : (
          <GameIntro settings={p.settings} />
        )}

        <div style={statsRowStyle}>
          <StatBox
            label="오늘 남은 도전"
            value={isUnlimited ? '무제한' : `${remaining} / ${p.coinStatus?.limit ?? '-'}`}
            tone="text"
          />
          <StatBox
            label="내 최고점"
            value={p.myBestScore != null ? p.myBestScore.toLocaleString() : '기록 없음'}
            tone="gold"
          />
        </div>

        {outsidePeriod && (
          <div style={warnBoxStyle} data-testid="acid-period-notice" role="status">
            {periodState === 'before' ? OFFICIAL_BEFORE_NOTE : OFFICIAL_AFTER_NOTE}
          </div>
        )}
        {p.startError && <div style={errorBoxStyle}>{p.startError}</div>}
        {!enabled && (
          <div style={warnBoxStyle}>
            관리자가 아직 엔글 타자왕을 공개하지 않았습니다. (관리자는 데모 플레이 가능)
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <button style={primaryBtn(canStart)} disabled={!canStart} onClick={p.onStart} data-testid="acid-official-start">
            {p.isStarting
              ? '시작 중…'
              : outsidePeriod
              ? (periodState === 'before' ? '오픈 전' : '대회 종료')
              : isCoinExhausted
              ? '내일 재도전'
              : p.phase === 'ended'
              ? '다시 도전'
              : '도전 시작!'}
          </button>
          {/* V70G8I — 연습 게임: 도전 횟수와 무관 (0개여도 가능). 공개 상태에서만 */}
          <button
            style={practiceBtn(canPractice)}
            disabled={!canPractice}
            onClick={p.onPractice}
            data-testid="acid-practice-start"
          >
            연습 게임
          </button>
          <button
            style={{ ...ghostBtn, opacity: canOpenLeaderboard ? 1 : 0.5, cursor: canOpenLeaderboard ? 'pointer' : 'not-allowed' }}
            disabled={!canOpenLeaderboard}
            onClick={canOpenLeaderboard ? p.onLeaderboard : undefined}
            title={canOpenLeaderboard ? undefined : '점수 저장 완료 후 이동 가능'}
          >
            리더보드 보기
          </button>
          <button style={ghostBtn} onClick={p.onExit}>
            나가기
          </button>
        </div>

        {/* V70G8I — 두 모드 구분 안내 */}
        <div style={modeGuideGridStyle} data-testid="acid-mode-guide">
          <div style={{ ...modeGuideStyle, borderColor: C.gold }}>
            <div style={{ color: C.gold, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>도전 시작!</div>
            <div>{OFFICIAL_MODE_NOTE}</div>
          </div>
          <div style={modeGuideStyle}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>연습 게임</div>
            <div>{PRACTICE_MODE_NOTE}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SaveStateBadge({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  if (state.status === 'idle') return null;
  const commonStyle: React.CSSProperties = {
    marginTop: 10,
    padding: '10px 12px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13,
    fontWeight: 500,
  };
  // V70 2026-10-19 — 상태 뱃지 톤 통일 (네온 cyan/green/yellow/red 제거)
  if (state.status === 'saving') {
    return (
      <div style={{ ...commonStyle, background: 'rgba(245, 200, 66, 0.08)', border: `1px solid ${C.gold}`, color: C.gold }}>
        <Loader2 size={16} className="spin-anim" style={{ animation: 'spin 1s linear infinite' }} />
        <span>점수 저장 중…</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (state.status === 'success') {
    // F3 정책: low 는 공식 반영 but 서버 재계산 안내 추가.
    // R1: rank=null 이면 N위 임의 표시 금지.
    return (
      <div style={{ ...commonStyle, background: 'rgba(156, 221, 226, 0.08)', border: `1px solid ${C.success}`, color: C.success, flexWrap: 'wrap' }}>
        <CheckCircle2 size={16} />
        <span>
          저장 완료
          {state.isPersonalBest ? ' · 개인 최고기록 갱신' : ''}
          {state.rank != null ? ` · 현재 순위 ${state.rank}위` : ''}
          {state.flaggedLow && state.serverVerifiedScore != null
            ? ` · 서버 재계산 점수 ${state.serverVerifiedScore.toLocaleString()}점으로 공식 반영 (보고 점수와 미세 차이)`
            : ''}
        </span>
      </div>
    );
  }
  if (state.status === 'flagged') {
    // F3 정책 적용 (BE acidRain.ts): low 는 공식 반영, high/invalid/macro 는 공식 보류.
    //   low 는 flagged state 로 분기되지만 **공식 리더보드에 반영됨** (서버 재계산 점수로).
    //   high/invalid/macro 는 tournamentScores 미기록 (세션만 보존).
    //   FE 메시지는 각 flagged 의 **실제 저장 결과** 를 정확히 전달 (사용자 지시 §6).
    const flagMsg: Record<string, string> = {
      high: '서버 점수 검증 결과 차이가 커서 공식 기록 반영이 보류되었습니다.',
      invalid: '입력 이벤트 검증에 실패해 공식 기록 반영이 보류되었습니다.',
      macro: '비정상적인 입력 패턴이 감지되어 관리자 검토 대상으로 분류되었습니다. 공식 기록 반영은 보류됩니다.',
    };
    return (
      <div style={{ ...commonStyle, background: 'rgba(245, 200, 66, 0.08)', border: `1px solid ${C.warning}`, color: C.warning, flexWrap: 'wrap' }}>
        <AlertCircle size={16} />
        <span>
          {flagMsg[state.reason] || '공식 기록 반영이 보류되었습니다.'}
          {state.serverVerifiedScore != null ? ` (서버 재계산 점수: ${state.serverVerifiedScore.toLocaleString()})` : ''}
        </span>
      </div>
    );
  }
  // error — danger 신호 유지, 톤 다운
  return (
    <div style={{ ...commonStyle, background: 'rgba(245, 165, 165, 0.08)', border: `1px solid ${C.danger}`, color: C.danger, flexWrap: 'wrap' }}>
      <AlertCircle size={16} />
      <span>저장 실패: {state.message}</span>
      <button
        onClick={onRetry}
        style={{ marginLeft: 'auto', padding: '4px 10px', background: 'transparent', border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
      >
        재시도
      </button>
    </div>
  );
}

function GameIntro({ settings }: { settings: AcidRainSettings | null }) {
  const dur = settings?.durationSec ?? 120;
  const lives = settings?.initialLives ?? 5;
  return (
    <div style={introStyle}>
      <p style={{ margin: '8px 0 12px', color: C.text, fontSize: 14, lineHeight: 1.6 }}>
        화면 위에서 떨어지는 산성비 단어를 입력해 지우세요. 바닥에 닿기 전에 정확히 타이핑해야 합니다.
      </p>
      <ul style={ruleListStyle}>
        <li>⏱ 제한 시간 <b>{dur}초</b> — 일시정지 없음, 포커스를 잃어도 시간은 계속 흐릅니다.</li>
        <li>❤ 생명력 <b>{lives}</b> — 단어가 바닥에 닿을 때마다 -1</li>
        <li>⚡ 콤보 — 연속 성공 시 보너스 점수 (최대 2배)</li>
        <li>🔺 매 10초마다 난이도 상승 (낙하 속도 ×1.1, 등장 간격 /1.1)</li>
        <li>⌨ 단어를 입력한 뒤 <b>Enter</b> 키를 누르면 해당 단어가 제거됩니다.</li>
      </ul>
    </div>
  );
}

function ResultSummary({ result }: { result: GameResult }) {
  const reasonText =
    result.reason === 'time_up'
      ? '제한 시간 종료'
      : result.reason === 'no_lives'
      ? '생명력 소진'
      : '사용자 중단';
  return (
    // V70 2026-10-19 — 네온 border/accent 제거 → panel + muted 톤.
    //   "게임 종료" 라벨은 textMuted (강조는 결과 셀 내부 숫자에만 제한)
    <div style={{ ...introStyle, border: `1px solid ${C.borderStrong}` }}>
      <div style={{ color: C.textMuted, fontSize: 13, fontWeight: 600, marginBottom: 6, letterSpacing: 0.5 }}>게임 종료 — {reasonText}</div>
      <div style={resultGridStyle}>
        <ResultCell label="최종 점수" value={result.clientReportedScore.toLocaleString()} big tone="gold" />
        <ResultCell label="제거한 단어" value={String(result.cleared)} />
        <ResultCell label="놓친 단어" value={String(result.missed)} />
        <ResultCell label="최대 콤보" value={`${result.maxCombo}x`} tone="success" />
      </div>
    </div>
  );
}

// V70 2026-10-19 — ResultCell 톤 통일: gold (최종 점수 강조) / success (최대 콤보) / text (일반)
function ResultCell({ label, value, big, tone }: { label: string; value: string; big?: boolean; tone?: 'gold' | 'success' }) {
  const color = tone === 'gold' ? C.gold : tone === 'success' ? C.success : C.text;
  return (
    <div style={{ background: C.bgSecondary, border: `1px solid ${C.panel}`, borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 2 }}>{label}</div>
      <div style={{ color, fontWeight: 700, fontSize: big ? 22 : 16 }}>{value}</div>
    </div>
  );
}

// V70 2026-10-19 — StatBox 톤 통일: gold (내 최고점 강조) / text (오늘 남은 도전)
function StatBox({ label, value, tone }: { label: string; value: string; tone: 'gold' | 'text' }) {
  const color = tone === 'gold' ? C.gold : C.text;
  return (
    <div style={{ flex: 1, minWidth: 120, background: C.bgSecondary, border: `1px solid ${C.panel}`, borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ color: C.textMuted, fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontWeight: 700, fontSize: 18 }}>{value}</div>
    </div>
  );
}

// ─────────────────────── 플레이 화면 ───────────────────────

interface GamePlayScreenProps {
  phase: 'countdown' | 'playing';
  countdownNum: number;
  session: SessionBundle;
  onCountdownTick: (n: number) => void;
  onCountdownDone: () => void;
  // Phase 3-C.1 결함 A — inputEvents 전달 (engine 수집)
  onEnd: (r: GameResult, inputEvents?: AcidRainServerInputEvent[]) => void;
  onForceQuit: () => void;
  /** V70G8I: 오답 제출 (연습 결과 정확도 집계용 — 게임 규칙·채점에는 영향 없음) */
  onWrongSubmit?: () => void;
}

function GamePlayScreen(p: GamePlayScreenProps) {
  // Phase 3-C.1 결함 A — engine instance ref 로 보관하여 onEnd 콜백에서 getInputEvents 접근.
  // useAcidRainEngine 가 engineRef (내부) 를 통해 instance 를 관리하므로, 외부에서는 return 값을
  // ref 로 들고 onEnd 시 getInputEvents() 호출.
  const engineHandleRef = useRef<ReturnType<typeof useAcidRainEngine> | null>(null);
  const engine = useAcidRainEngine({
    settings: p.session.settings,
    sessionId: p.session.sessionId,
    seed: p.session.seed,
    startedAtServerMs: p.session.startedAtServerMs,
    fieldHeightPx: FIELD_HEIGHT_PX,
    onEnd: (r) => {
      // engine.getEvents() 는 engine instance 를 통해 수집됨 — handle ref 에서 getInputEvents 호출.
      const events = engineHandleRef.current?.getInputEvents() ?? [];
      p.onEnd(r, events);
    },
  });
  engineHandleRef.current = engine;
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [fieldWidth, setFieldWidth] = useState(0);
  const [wrongFlash, setWrongFlash] = useState(false);
  const wrongFlashTimerRef = useRef<number | null>(null);

  // fieldWidth 측정 (ResizeObserver)
  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    const update = () => setFieldWidth(el.clientWidth);
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const triggerWrongFlash = useCallback(() => {
    setWrongFlash(true);
    if (wrongFlashTimerRef.current != null) window.clearTimeout(wrongFlashTimerRef.current);
    wrongFlashTimerRef.current = window.setTimeout(() => setWrongFlash(false), 180);
  }, []);

  useEffect(() => () => {
    if (wrongFlashTimerRef.current != null) window.clearTimeout(wrongFlashTimerRef.current);
  }, []);

  // V70G8J — 게임 입력창 붙여넣기·끌어놓기 차단 (공식·연습 공통). 점수 계산·서버 검증은 변경 없음.
  const [pasteBlocked, setPasteBlocked] = useState(false);
  const pasteBlockedTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const detach = attachPasteGuard(el, () => {
      setPasteBlocked(true);
      if (pasteBlockedTimerRef.current != null) window.clearTimeout(pasteBlockedTimerRef.current);
      pasteBlockedTimerRef.current = window.setTimeout(() => setPasteBlocked(false), 1500);
    });
    return () => {
      detach();
      if (pasteBlockedTimerRef.current != null) window.clearTimeout(pasteBlockedTimerRef.current);
    };
  }, []);

  const handleSubmit = useCallback(() => {
    const r = engine.submit();
    if (!r.hadInput) {
      // 빈 입력 → noop (사용자 지시 §3)
      return;
    }
    if (r.matched) {
      setInputValue('');
    } else {
      // 오답: input 비움 + 시각 flash. 콤보/생명력 유지
      setInputValue('');
      engine.clearBuffer();
      triggerWrongFlash();
      p.onWrongSubmit?.();
    }
    // 포커스 유지
    inputRef.current?.focus();
  }, [engine, triggerWrongFlash, p]);

  // Countdown (3→2→1→go)
  useEffect(() => {
    if (p.phase !== 'countdown') return;
    const t = setTimeout(() => {
      if (p.countdownNum > 1) {
        p.onCountdownTick(p.countdownNum - 1);
      } else {
        p.onCountdownDone();
      }
    }, 900);
    return () => clearTimeout(t);
  }, [p]);

  // Playing 전환 시 엔진 시작 + 입력창 포커스
  useEffect(() => {
    if (p.phase === 'playing' && !engine.isRunning) {
      engine.startAfterCountdown();
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [p.phase, engine]);

  const snap = engine.snapshot;

  return (
    <div style={pageStyle}>
      <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
        {/* V70G8I — 연습 중임을 항상 표시 */}
        {p.session.practice && (
          <div data-testid="acid-practice-banner" style={practiceBannerStyle}>
            연습 게임 · 도전 횟수 차감 없음 · 기록·리더보드 미반영
          </div>
        )}
        {/* HUD — V70 2026-10-19 톤 통일 (강조 색상 '점수'=gold 한 곳에만, 생명력=danger 신호 유지) */}
        <div style={hudStyle}>
          <HudCell icon={<Clock size={16} />} label="남은 시간" value={`${Math.ceil(snap?.remainingSec ?? 0)}s`} tone="text" />
          <HudCell icon={<Trophy size={16} />} label="점수" value={(snap?.score ?? 0).toLocaleString()} tone="gold" big />
          <HudCell icon={<Zap size={16} />} label="콤보" value={`${snap?.combo ?? 0}x`} tone="success" />
          <HudCell icon={<Target size={16} />} label="제거" value={String(snap?.cleared ?? 0)} />
          <HudCell icon={<Heart size={16} />} label="생명력" value={renderHearts(snap?.lives ?? 0, p.session.settings.initialLives)} tone="danger" />
          <HudCell icon={<Flame size={16} />} label="난이도" value={`Lv.${snap?.difficultyLevel ?? 0}`} />
          <button style={quitBtnStyle} onClick={() => {
            const r = engine.stopEarly();
            if (r) p.onEnd(r, engine.getInputEvents());
            else p.onForceQuit();
          }}>
            <X size={14} /> 중단
          </button>
        </div>

        {/* Field */}
        <div ref={fieldRef} style={fieldWrapperStyle}>
          {/* Countdown 오버레이 */}
          {p.phase === 'countdown' && (
            <div style={countdownOverlayStyle}>
              <div style={countdownNumStyle}>{p.countdownNum}</div>
              <div style={countdownHintStyle}>준비…</div>
            </div>
          )}

          {/* 낙하 단어 */}
          {snap?.fallingWords.map((w) => {
            const ageSec = (snap.elapsedMs - w.spawnMs) / 1000;
            const yPx = ageSec * w.speed;
            return (
              <WordDrop
                key={w.id}
                text={w.text}
                xRatio={w.xRatio}
                topPx={yPx}
                dangerPct={yPx / FIELD_HEIGHT_PX}
                fieldWidth={fieldWidth}
              />
            );
          })}

          {/* 바닥 라인 */}
          <div style={groundLineStyle} />
        </div>

        {/* 입력창 */}
        <div style={inputBarStyle}>
          <span style={inputPromptStyle}>▶</span>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              engine.setInput(e.target.value);
            }}
            onCompositionStart={() => engine.onCompositionStart()}
            onCompositionEnd={(e) => {
              const text = (e.target as HTMLInputElement).value;
              engine.onCompositionEnd(text);
              // 자동 제출 X — Enter 전용 (사용자 지시 §3)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // IME 조합 중 Enter 는 무시 (confirmation key 역할)
                if (e.nativeEvent.isComposing || (e as any).keyCode === 229) return;
                e.preventDefault();
                handleSubmit();
              }
              // Space 는 더 이상 제출 트리거 아님 (사용자 지시 §3)
            }}
            placeholder="한글로 입력 후 Enter"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            style={{
              ...inputStyle,
              // V70 2026-10-19 — wrongFlash 피드백은 유지 (오답 신호). 네온 cyan inset 제거.
              borderColor: wrongFlash ? C.danger : C.panel,
              boxShadow: wrongFlash
                ? `inset 0 0 8px rgba(245, 165, 165, 0.3), 0 0 6px rgba(245, 165, 165, 0.4)`
                : 'inset 0 0 6px rgba(245, 200, 66, 0.1)',
              transition: 'border-color 120ms ease, box-shadow 120ms ease',
            }}
          />
        </div>
        <div
          style={{ padding: '6px 16px 12px', color: pasteBlocked ? C.danger : C.textMuted, fontSize: 11, textAlign: 'center' }}
          data-testid="acid-input-hint"
          aria-live="polite"
        >
          {pasteBlocked ? '붙여넣기는 사용할 수 없습니다. 직접 입력해 주세요.' : 'Enter 로 제출'}
        </div>
      </div>
    </div>
  );
}

// V70 2026-10-19 — HudCell 톤 통일: gold / success / danger / text
function HudCell({ icon, label, value, tone, big }: { icon: React.ReactNode; label: string; value: React.ReactNode; tone?: 'gold' | 'success' | 'danger' | 'text'; big?: boolean }) {
  const color =
    tone === 'gold' ? C.gold :
    tone === 'success' ? C.success :
    tone === 'danger' ? C.danger : C.text;
  return (
    <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 72, borderRight: `1px solid ${C.panel}` }}>
      <div style={{ color: C.textMuted, fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color }}>{icon}</span> {label}
      </div>
      <div style={{ color, fontWeight: 700, fontSize: big ? 18 : 14 }}>{value}</div>
    </div>
  );
}

function renderHearts(lives: number, initial: number): string {
  const full = '♥'.repeat(Math.max(0, lives));
  const empty = '♡'.repeat(Math.max(0, initial - lives));
  return full + empty;
}

/**
 * 단어 폭 추정 (경계 clamp 용)
 *   한글 fontSize 20px + letter-spacing 0.5px → 글자당 ~21px
 *   좌우 padding 6px씩, 안전 여유 4px
 */
function estimateWordWidthPx(text: string): number {
  return text.length * 21 + 12 + 4;
}

function WordDrop({
  text,
  xRatio,
  topPx,
  dangerPct,
  fieldWidth,
}: {
  text: string;
  xRatio: number;
  topPx: number;
  dangerPct: number;
  fieldWidth: number;
}) {
  // V70 2026-10-19 — 디자인 토큰 (wordHard/wordMedium/wordEasy) + 네온 glow 축소.
  //   색상 자체는 danger/safe 신호 유지 (게임 피드백 핵심), textShadow 는 subtle solid 로 다운.
  const danger = Math.min(1, Math.max(0, dangerPct));
  const color = danger > 0.7 ? C.wordHard : danger > 0.4 ? C.wordMedium : C.wordEasy;
  const shadow = danger > 0.7
    ? '0 1px 3px rgba(0, 0, 0, 0.6)'
    : danger > 0.4
    ? '0 1px 3px rgba(0, 0, 0, 0.5)'
    : '0 1px 2px rgba(0, 0, 0, 0.4)';
  // X 좌표 clamp: 전체 단어가 필드 안에 들어오도록 중심 위치 제한
  // field 가 아직 측정되지 않았으면 안전하게 중앙 고정
  const estW = estimateWordWidthPx(text);
  const halfW = estW / 2;
  let leftPx: number;
  if (fieldWidth > 0) {
    const idealCenter = xRatio * fieldWidth;
    const maxCenter = Math.max(halfW, fieldWidth - halfW);
    const minCenter = Math.min(halfW, maxCenter);
    leftPx = Math.min(Math.max(idealCenter, minCenter), maxCenter);
    // 단어가 필드보다 넓은 극단적인 경우 → 좌측 정렬로 fallback (오른쪽 잘림 방지)
    if (estW >= fieldWidth) leftPx = halfW;
  } else {
    leftPx = 0;
  }
  return (
    <div
      style={{
        position: 'absolute',
        left: leftPx,
        top: topPx,
        transform: 'translateX(-50%)',
        color,
        fontSize: 20,
        fontWeight: 700,
        textShadow: shadow,
        whiteSpace: 'nowrap',
        letterSpacing: 0.5,
        userSelect: 'none',
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        padding: '0 6px',
        maxWidth: fieldWidth > 0 ? fieldWidth : undefined,
      }}
    >
      {text}
    </div>
  );
}

// ─────────────────────── 스타일 ───────────────────────

// V70 2026-10-19 — 미니멀 프리미엄 아케이드 팔레트 적용 (디자인 토큰 사용).
//   네온 cyan/pink/yellow glow 전부 제거 → GOLD/TEXT 톤 다운.
//   red (위험/에러/groundLine) 는 상태 신호 유지 위해 톤 다운만.
const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: `linear-gradient(180deg, ${C.bgPrimary} 0%, ${C.bgSecondary} 100%)`,
  padding: 24,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
};

// V70 2026-10-19 — 타자왕 화면 가로 확대 (사용자 피드백: 화면이 작고 주변 빈 공간 많음).
//   maxWidth 820 → 1200 (약 46% 확대). 모바일에서는 width:100% 자동 축소 → 가로 스크롤 없음.
//   세로 (FIELD_HEIGHT_PX = 480) 는 BE engine 과 공유되는 상수이므로 미변경 — 서버 재계산 호환성 보장.
const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 1200,
  background: C.panelDeep,
  border: `1px solid ${C.panel}`,
  borderRadius: 16,
  padding: 24,
  color: C.text,
  boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
};

const demoBannerStyle: React.CSSProperties = {
  background: 'rgba(245, 200, 66, 0.08)',  // GOLD soft bg
  border: '1px solid rgba(245, 200, 66, 0.25)',
  color: C.gold,
  fontSize: 12,
  padding: '6px 10px',
  borderRadius: 6,
  marginBottom: 12,
  textAlign: 'center',
};

const titleStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 800,
  margin: '4px 0 16px',
  letterSpacing: 1,
  color: C.text,
  // 과도한 네온 textShadow 제거 (0 0 16px cyan → 미세 soft shadow)
  textShadow: '0 1px 2px rgba(0,0,0,0.4)',
};

const introStyle: React.CSSProperties = {
  background: C.bgSecondary,
  border: `1px solid ${C.borderStrong}`,
  borderRadius: 10,
  padding: 14,
  marginBottom: 14,
};

const ruleListStyle: React.CSSProperties = {
  margin: 0,
  padding: '0 0 0 4px',
  listStyle: 'none',
  color: C.text,
  fontSize: 13,
  lineHeight: 1.9,
};

const resultGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 8,
  marginTop: 8,
};

const statsRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  marginTop: 4,
  flexWrap: 'wrap',
};

const errorBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 10,
  // 에러 신호성 유지 — red 톤 다운
  background: 'rgba(245, 165, 165, 0.08)',
  border: `1px solid ${C.danger}`,
  color: C.danger,
  borderRadius: 6,
  fontSize: 13,
};

const warnBoxStyle: React.CSSProperties = {
  marginTop: 12,
  padding: 10,
  background: 'rgba(245, 200, 66, 0.08)',
  border: '1px solid rgba(245, 200, 66, 0.3)',
  color: C.gold,
  borderRadius: 6,
  fontSize: 13,
};

const primaryBtn = (enabled: boolean): React.CSSProperties => ({
  flex: 1,
  minWidth: 180,
  padding: '12px 20px',
  // 네온 cyan 제거 → GOLD 단색 (프리미엄 톤)
  background: enabled ? C.gold : C.panel,
  color: enabled ? C.textOnGold : C.textMuted,
  fontWeight: 700,
  fontSize: 15,
  border: 0,
  borderRadius: 8,
  cursor: enabled ? 'pointer' : 'not-allowed',
  // 네온 glow 제거 → 미세 soft shadow
  boxShadow: enabled ? '0 2px 6px rgba(245, 200, 66, 0.25)' : 'none',
  transition: 'background 150ms ease-out',
});

// V70G8I — 연습 게임 버튼: 공식(골드 채움)과 구분되는 외곽선 버튼
const practiceBtn = (enabled: boolean): React.CSSProperties => ({
  flex: 1,
  minWidth: 140,
  padding: '12px 20px',
  background: 'transparent',
  color: enabled ? C.gold : C.textMuted,
  fontWeight: 700,
  fontSize: 15,
  border: `1px solid ${enabled ? C.gold : C.panel}`,
  borderRadius: 8,
  cursor: enabled ? 'pointer' : 'not-allowed',
});

const practiceBannerStyle: React.CSSProperties = {
  padding: '6px 12px',
  background: 'rgba(245, 200, 66, 0.08)',
  borderBottom: '1px solid rgba(245, 200, 66, 0.25)',
  color: C.gold,
  fontSize: 12,
  fontWeight: 600,
  textAlign: 'center',
};

const practiceNoteStyle: React.CSSProperties = {
  marginBottom: 10,
  padding: '8px 12px',
  background: 'rgba(245, 200, 66, 0.08)',
  border: '1px solid rgba(245, 200, 66, 0.3)',
  color: C.gold,
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
};

const modeGuideGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 8,
  marginTop: 12,
};

const modeGuideStyle: React.CSSProperties = {
  background: C.bgSecondary,
  border: `1px solid ${C.panel}`,
  borderRadius: 8,
  padding: '10px 12px',
  color: C.textMuted,
  fontSize: 12,
  lineHeight: 1.6,
};

const ghostBtn: React.CSSProperties = {
  padding: '12px 18px',
  background: 'transparent',
  color: C.text,
  border: `1px solid ${C.panel}`,
  borderRadius: 8,
  fontSize: 14,
  cursor: 'pointer',
};

const hudStyle: React.CSSProperties = {
  display: 'flex',
  background: C.bgPrimary,
  borderBottom: `1px solid ${C.panel}`,
  alignItems: 'stretch',
  overflowX: 'auto',
};

const fieldWrapperStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: FIELD_HEIGHT_PX,
  // red radial 톤 다운 (0.15 → 0.08) + CHARCOAL gradient
  background: `
    radial-gradient(circle at 50% 100%, rgba(245, 165, 165, 0.08) 0%, transparent 50%),
    linear-gradient(180deg, ${C.bgPrimary} 0%, ${C.bgSecondary} 100%)
  `,
  overflow: 'hidden',
};

const groundLineStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 2,
  // 위험 신호 유지 — red 톤 다운 (opacity 축소)
  background: `linear-gradient(90deg, transparent 0%, ${C.danger} 50%, transparent 100%)`,
  boxShadow: '0 0 6px rgba(245, 165, 165, 0.5)',
};

const countdownOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(17, 29, 37, 0.75)',
  zIndex: 10,
};

const countdownNumStyle: React.CSSProperties = {
  fontSize: 96,
  fontWeight: 900,
  color: C.gold,
  // 네온 cyan glow 제거 → GOLD 미세 soft shadow
  textShadow: '0 2px 8px rgba(245, 200, 66, 0.35)',
};

const countdownHintStyle: React.CSSProperties = {
  color: C.textMuted,
  fontSize: 14,
  marginTop: 4,
  letterSpacing: 2,
};

const inputBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '14px 16px',
  background: C.bgPrimary,
  borderTop: `1px solid ${C.panel}`,
};

const inputPromptStyle: React.CSSProperties = {
  color: C.gold,
  fontSize: 18,
  fontWeight: 700,
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  background: C.bgSecondary,
  border: `1px solid ${C.panel}`,
  borderRadius: 8,
  color: C.text,
  padding: '12px 14px',
  fontSize: 18,
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
  outline: 'none',
  // 네온 cyan inset 제거 → GOLD soft inset
  boxShadow: 'inset 0 0 6px rgba(245, 200, 66, 0.1)',
};

const quitBtnStyle: React.CSSProperties = {
  marginLeft: 'auto',
  marginRight: 8,
  padding: '6px 10px',
  background: 'transparent',
  border: `1px solid ${C.borderStrong}`,
  borderRadius: 6,
  color: C.textMuted,
  fontSize: 12,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  alignSelf: 'center',
};

export default AcidRainGamePage;
